/**
 * 売掛・買掛・未払残高(B4・2026-10-23)。月末時点の残高を「PL に入った額 − 実際にお金が動いた額」で出す。
 * 具体例は docs/specs/2026-10-全体設計.md 13章と tests/unit/balances.test.ts。読むだけで書き込みはしない。
 *
 *  - PL に入ったか: plCategoryOf(t, 月末) が 'actual'(計上日の月が来た「承認済み以降」)。申請中・差戻し・取消・資金移動は入れない
 *  - 買掛・未払(経費・原価): お金が出た日まで残る
 *      - 立替(advance): 経理の精算の日(精算の出金)
 *      - 出金の記録(cash_entry_id)がある: その記録が「確定」になった日
 *      - クレカ(支払い済み): 引き落とし日が来たら払ったとみなす(明細の取り込みで確定していなくても。2026-10-23 発注者決定⑤)
 *      - 出金の記録がない(支払い済み申請の現金・振込など): 支払い済みなら支払日
 *  - 仮払い: 仮払金(経理が仮払いしてから、本人が支払うまで)として別に出す。PL に入る前・差戻しの後でも、お金が出ていれば出す。
 *          経理が仮払いする前は普通の未払。仮払いの後も買掛・未払に残すかは BALANCE_RULES.prepaid
 *  - 売上・原価入力の原価(支払い済みの状態で作られ、出金の記録が無い)は、入力した時点で払ったものとして扱う(2026-10-23 時点。要確認)
 *  - 売掛(売上): 請求書の入金が記録されるまで残る。一部入金は請求書の明細の上から順に充てる。多すぎた入金はマイナスで残す
 *      - 請求書に入っていない売上(未請求)を入れるかは BALANCE_RULES.includeUnbilled
 *      - 切り替え日(BALANCE_RULES.receivableFrom)より前に計上した売上は入れない(2026-10-22 決定3)
 *  - 承認後に差戻し・取消されたものは、過去の月末からも消える(当面許容・2026-10-22)
 */
import { plCategoryOf } from './plRecognition';
import type { CashEntry, DateStr, DepartmentId, Invoice, Transaction, Yen } from '@core/lib/types';

/**
 * 仮払いの出し方(全体設計 13章 #4)
 *  - 'separate': 経理が仮払いした後は仮払金だけに出し、買掛・未払には入れない(案B・2026-10-22 決定1「仮払いは売掛・買掛に含めない」)。
 *                 仮払いする前(承認済み・まだ渡していない)はお金が動いていないので未払に出す
 *  - 'both':     本人が支払うまで買掛・未払にも出す(案A・会計の形どおり)
 */
export type PrepaidRule = 'separate' | 'both';

export type BalanceRules = {
  prepaid: PrepaidRule;
  /** 請求書を作る前の売上も売掛に入れる(全体設計 13章 #8) */
  includeUnbilled: boolean;
  /** 売掛の対象を始める日(切り替え日)。null は制限なし(確認用テスト環境) */
  receivableFrom: DateStr | null;
};

/**
 * 決まり(2026-10-23 10:35 発注者決定: ①仮払いは案B ②未請求の売上も入れる ③切り替え日は段階7の本番実装の日)。
 * 変えるときは判断依頼。receivableFrom は段階7で日付を入れる(入れないと過去の手入力の売上が未請求の売掛に出る)
 */
export const BALANCE_RULES: BalanceRules = {
  prepaid: 'separate',
  includeUnbilled: true,
  receivableFrom: null,
};

export type ReceivableRow = {
  transaction: Transaction;
  /** 請求書番号。未請求は null */
  invoiceNumber: string | null;
  amount: Yen;
  received: Yen;
  balance: Yen;
};

export type PayableRow = {
  transaction: Transaction;
  amount: Yen;
  paid: Yen;
  balance: Yen;
};

export type PrepaidRow = {
  transaction: Transaction;
  /** 仮払いした日 */
  prepaidDate: DateStr;
  amount: Yen;
  /** 本人が支払った(精算された)額 */
  settled: Yen;
  balance: Yen;
};

export type DepartmentBalance = {
  departmentId: DepartmentId;
  receivable: Yen;
  payable: Yen;
  prepaid: Yen;
};

export type Balances = {
  asOf: DateStr;
  receivables: ReceivableRow[];
  payables: PayableRow[];
  prepaids: PrepaidRow[];
  /** 残高のある事業部だけ(並びは事業部ID順。画面でマスタの順に並べ直す) */
  byDepartment: DepartmentBalance[];
  total: { receivable: Yen; payable: Yen; prepaid: Yen };
};

const PAID_STATUSES = new Set(['paid', 'completed']);

/** 出金・入金の記録が「確定」で、その日が月末までならその日。予定・見つからない・月末より後は null */
function settledDateOf(entryId: string | null | undefined, entries: Map<string, CashEntry>, asOf: DateStr): DateStr | null {
  if (!entryId) return null;
  const e = entries.get(entryId);
  if (!e || e.status !== 'settled' || e.date > asOf) return null;
  return e.date;
}

/** 本人(または支払い者)が支払い済みで、その支払日が月末まで */
function paidBy(t: Transaction, asOf: DateStr): boolean {
  return PAID_STATUSES.has(t.status) && !!t.payment_date && t.payment_date <= asOf;
}

/** 経費・原価のお金が出た日(月末までに出ていなければ null) */
export function expenseOutDate(t: Transaction, entries: Map<string, CashEntry>, asOf: DateStr): DateStr | null {
  // 立替(部門長立替・本人の立替): 経理が精算した日。精算の出金の記録が無い古いデータは精算日で見る
  if (t.advance) {
    if (t.reimbursement_cash_entry_id && entries.has(t.reimbursement_cash_entry_id)) {
      return settledDateOf(t.reimbursement_cash_entry_id, entries, asOf);
    }
    // 精算の出金の記録が無い・読めない(古いデータ・削除された等): 精算日で見る
    return t.reimbursed_at && t.reimbursement_date && t.reimbursement_date <= asOf ? t.reimbursement_date : null;
  }
  // 出金の記録がある(経理の振込・現金、クレカ、繰り返し支払い、振込手数料): 確定した日
  if (t.cash_entry_id) {
    const e = entries.get(t.cash_entry_id);
    // クレカは引き落とし日(予定の出金の日付)が来たら払ったとみなす(決定⑤)
    if (e && e.status === 'planned' && t.payment_method === 'card' && PAID_STATUSES.has(t.status)) {
      return e.date <= asOf ? e.date : null;
    }
    if (e) return settledDateOf(t.cash_entry_id, entries, asOf);
    // 記録が読めない(削除された等): 支払い済みなら支払日で見る
    return paidBy(t, asOf) ? t.payment_date : null;
  }
  // 出金の記録がない(支払い済み申請の現金・振込など): 支払い済みなら支払日
  return paidBy(t, asOf) ? t.payment_date : null;
}

/**
 * 請求書ごとに、月末までの入金を明細(売上の取引)へ上から順に充てる。多すぎた分は最後の明細に足す。
 * known を渡すと、その中に無い取引(削除・差戻しされた売上)の明細には充てない
 */
export function allocateReceipts(
  invoices: Invoice[],
  asOf: DateStr,
  known?: Set<string>,
): Map<string, { received: Yen; invoiceNumber: string }> {
  const out = new Map<string, { received: Yen; invoiceNumber: string }>();
  for (const inv of invoices) {
    if (inv.status === 'void') continue;
    let rest = (inv.receipts ?? []).filter((r) => r.date <= asOf).reduce((s, r) => s + r.amount, 0);
    if (rest < 0) rest = 0;
    const lines = inv.lines.filter((l) => l.transaction_id && (!known || known.has(l.transaction_id)));
    lines.forEach((l, i) => {
      const isLast = i === lines.length - 1;
      const take = isLast ? rest : Math.min(Math.max(l.amount, 0), rest);
      rest -= take;
      const id = l.transaction_id as string;
      const prev = out.get(id);
      out.set(id, { received: (prev?.received ?? 0) + take, invoiceNumber: inv.number });
    });
  }
  return out;
}

export function computeBalances(params: {
  /** 月末の日付(例 2026-10-31) */
  asOf: DateStr;
  transactions: Transaction[];
  invoices: Invoice[];
  cashEntries: CashEntry[];
  rules?: BalanceRules;
}): Balances {
  const { asOf } = params;
  const rules = params.rules ?? BALANCE_RULES;
  const entries = new Map(params.cashEntries.map((e) => [e.id, e]));
  // 入金を充てる先: PL に入りうる売上(承認済み以降)。切り替え日より前の売上にも充てる(入金の順番を崩さないため。表には出さない)
  const knownSales = new Set(
    params.transactions.filter((t) => t.type === 'income' && plCategoryOf(t, '9999-12-31') === 'actual').map((t) => t.id),
  );
  const allocated = allocateReceipts(params.invoices, asOf, knownSales);

  const receivables: ReceivableRow[] = [];
  const payables: PayableRow[] = [];
  const prepaids: PrepaidRow[] = [];
  const seen = new Set<string>();

  for (const t of params.transactions) {
    if (seen.has(t.id)) continue;
    seen.add(t.id);
    if (t.type === 'transfer') continue;

    // 仮払金: PL の判定より前に見る。仮払いしたお金は、計上月の前でも、仮払いの後に差戻しされても、本人が支払うまで残る
    const prepaidDate = t.type === 'expense' ? settledDateOf(t.prepaid_cash_entry_id, entries, asOf) : null;
    if (prepaidDate && !paidBy(t, asOf)) {
      prepaids.push({ transaction: t, prepaidDate, amount: t.amount, settled: 0, balance: t.amount });
    }

    if (plCategoryOf(t, asOf) !== 'actual' || t.posting_date > asOf) continue;

    if (t.type === 'income') {
      if (rules.receivableFrom && t.posting_date < rules.receivableFrom) continue;
      const alloc = allocated.get(t.id);
      if (!alloc && !rules.includeUnbilled) continue;
      // 売上の入力で入金まで記録されたもの(入金の記録が確定)は入金済み
      const cashIn = settledDateOf(t.cash_entry_id, entries, asOf) ? t.amount : 0;
      const received = (alloc?.received ?? 0) + cashIn;
      const balance = t.amount - received;
      if (balance !== 0) receivables.push({ transaction: t, invoiceNumber: alloc?.invoiceNumber ?? null, amount: t.amount, received, balance });
      continue;
    }

    if (t.type !== 'expense') continue;

    if (t.payment_method === 'prepaid') {
      if (paidBy(t, asOf)) continue;
      // 仮払いする前はお金が動いていないので未払。仮払いの後は、案A のときだけ未払にも残す
      if (!prepaidDate || rules.prepaid === 'both') {
        payables.push({ transaction: t, amount: t.amount, paid: 0, balance: t.amount });
      }
      continue;
    }

    if (expenseOutDate(t, entries, asOf)) continue;
    payables.push({ transaction: t, amount: t.amount, paid: 0, balance: t.amount });
  }

  const byDept = new Map<DepartmentId, DepartmentBalance>();
  const dept = (id: DepartmentId) => {
    let d = byDept.get(id);
    if (!d) {
      d = { departmentId: id, receivable: 0, payable: 0, prepaid: 0 };
      byDept.set(id, d);
    }
    return d;
  };
  for (const r of receivables) dept(r.transaction.department_id).receivable += r.balance;
  for (const r of payables) dept(r.transaction.department_id).payable += r.balance;
  for (const r of prepaids) dept(r.transaction.department_id).prepaid += r.balance;

  const byPosting = <T extends { transaction: Transaction }>(a: T, b: T) =>
    a.transaction.posting_date === b.transaction.posting_date
      ? (a.transaction.code ?? '').localeCompare(b.transaction.code ?? '')
      : a.transaction.posting_date < b.transaction.posting_date
        ? -1
        : 1;
  receivables.sort(byPosting);
  payables.sort(byPosting);
  prepaids.sort((a, b) => (a.prepaidDate < b.prepaidDate ? -1 : a.prepaidDate > b.prepaidDate ? 1 : 0));

  const sum = (xs: { balance: Yen }[]) => xs.reduce((s, x) => s + x.balance, 0);
  return {
    asOf,
    receivables,
    payables,
    prepaids,
    byDepartment: [...byDept.values()].sort((a, b) => (a.departmentId < b.departmentId ? -1 : 1)),
    total: { receivable: sum(receivables), payable: sum(payables), prepaid: sum(prepaids) },
  };
}
